import React, { useState } from 'react';
import { Modal, Form, Button, Alert } from 'react-bootstrap';
import { writeContract, waitForTransactionReceipt } from '@wagmi/core';
import communityEnrollmentContractABI from '../../contracts/CommunityEnrollment.json';
import * as config from '../../config.tsx';

interface EnrollCommunityModalProps {
    show: boolean;
    onHide: () => void;
    parameterGroupName: string; // Name of the parameter group to enroll in
}

const EnrollCommunityModal: React.FC<EnrollCommunityModalProps> = ({ show, onHide, parameterGroupName }) => {
    const [communityName, setCommunityName] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const handleEnroll = async () => {
        setLoading(true);
        setError('');
        setSuccess('');

        try {
            // Write contract call to enroll the community
            const tx = await writeContract(config.config, {
                address: process.env.REACT_APP_COMMUNITY_ENROLLMENT_CONTRACT_ADDRESS as `0x${string}`,
                abi: communityEnrollmentContractABI.abi,
                functionName: 'enrollCommunityInParameterGroup',
                args: [communityName, parameterGroupName],
            });

            // Wait for the transaction receipt
            const receipt = await waitForTransactionReceipt(config.config, {
                hash: tx,
            });

            setSuccess(`Community "${communityName}" successfully enrolled in "${parameterGroupName}".`);
            setCommunityName('');
        } catch (error) {
            console.error('Error enrolling community:', error);
            setError('Failed to enroll the community. Please check the inputs and try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide}>
            <Modal.Header closeButton>
                <Modal.Title>Enroll Community in {parameterGroupName}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <Alert variant="danger">{error}</Alert>}
                {success && <Alert variant="success">{success}</Alert>}
                <Form>
                    <Form.Group>
                        <Form.Label>Community Name</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Enter community name"
                            value={communityName}
                            onChange={(e) => setCommunityName(e.target.value)}
                            disabled={loading}
                        />
                    </Form.Group>
                    <Button
                        variant="primary"
                        onClick={handleEnroll}
                        disabled={loading || !communityName}
                        className="mt-3"
                    >
                        {loading ? 'Enrolling...' : 'Enroll Community'}
                    </Button>
                </Form>
            </Modal.Body>
        </Modal>
    );
};

export default EnrollCommunityModal;
